import React from "react";
import Typography from "../../../common/Typography";
import MarkdownWrapper from "../../../common/MarkdownWrapper";
import Button from "../../../common/Button";
import { ResponsiveImage } from "../../../common";
import FlipCard from "../ui-helpers/FlipCard";
import { FlashcardContent } from "../../../../types/newtypes";
import { useFlashcardHook } from "./hooks/useFlashcardHook";
import { ArrowLeftIcon, ArrowRightIcon } from "lucide-react";

interface FlashcardPreviewProps {
  content: FlashcardContent;
  onCompleted?: () => void;
  // eslint-disable-next-line no-unused-vars
  onCardStateChange?: (isCompleted: boolean, canProceed: boolean) => void;
}

const FlashcardPreview: React.FC<FlashcardPreviewProps> = ({
  content,
  onCardStateChange,
}) => {
  const {
    flippedCards,
    currentCardIndex,
    handleCardFlip,
    handlePreviousCard,
    handleNextCard,
    isPreviousDisabled,
    isNextDisabled,
  } = useFlashcardHook({
    content,
    onCardStateChange,
  });

  const renderCardContent = (
    text: string,
    imageUri: string | null,
    imageAltText: string | undefined,
    isBack = false,
  ) => {
    // Center content when there's no image, otherwise left-align with image at top
    const hasImage = !!imageUri;
    const containerAlignment = hasImage ? "justify-start" : "justify-center";
    const textAlignment = hasImage ? "text-left" : "text-center";

    return (
      <div
        className={`flex flex-col items-center ${containerAlignment} p-6 ${textAlignment} w-full h-full`}
      >
        {hasImage && (
          <ResponsiveImage
            image_uri={imageUri}
            alt={imageAltText || (isBack ? "Back image" : "Front image")}
            maxWidth="100%"
            maxHeight="10rem"
            objectFit="contain"
            containerClassName="mb-4 w-full max-w-xs max-h-40 flex justify-center"
            className="max-h-40 w-auto max-w-full rounded-lg"
            showErrorState={true}
            placeholderText="Loading image..."
          />
        )}
        <div
          className={`text-base leading-relaxed break-words w-full max-h-60 overflow-y-auto ${textAlignment}`}
        >
          <MarkdownWrapper content={text || ""} />
        </div>
      </div>
    );
  };

  // Get the current card's label if it exists
  const currentCardLabel = content?.items?.[currentCardIndex]?.label;

  return (
    <div className="w-full">
      {/* Responsive Layout */}
      <div className="lg:grid lg:grid-cols-2 lg:gap-8">
        {/* Left Side - Title and Intro Text */}
        <div className="mb-8 lg:mb-0">
          <Typography variant="h4" weight="bold" className="mb-4">
            {content?.slide_title}
          </Typography>
          <Typography variant="p" className="!text-base mb-6">
            {content?.intro_text}
          </Typography>
        </div>

        {/* Right Side - Flash Cards */}
        <div className="flex flex-col items-center w-full">
          {/* Card Counter */}
          <div className="mb-4 text-center flex flex-col gap-2">
            <Typography variant="small" className="text-gray-600">
              Click the cards to reveal the definitions.
            </Typography>
            <Typography variant="small" className="text-gray-600">
              Card {currentCardIndex + 1} of {content?.items?.length}
            </Typography>
          </div>

          {/* Category Heading - only show if card has a label */}
          {currentCardLabel && (
            <Typography variant="h6" weight="bold" className="mb-2">
              {currentCardLabel}
            </Typography>
          )}

          {/* Current Card */}
          <div className="flex flex-col items-center mb-6 w-full max-w-lg mx-auto">
            <FlipCard
              key={`card-${currentCardIndex}`} // Force re-render when card changes
              frontContent={renderCardContent(
                content?.items[currentCardIndex]?.front_text,
                content?.items[currentCardIndex]?.front_image_uri,
                content?.items[currentCardIndex]?.front_alt_text,
              )}
              backContent={renderCardContent(
                content?.items[currentCardIndex]?.back_text,
                content?.items[currentCardIndex]?.back_image_uri,
                content?.items[currentCardIndex]?.back_alt_text,
                true,
              )}
              width="w-full max-w-md"
              height="h-80"
              isFlipped={flippedCards?.has(currentCardIndex)}
              onFlip={() => handleCardFlip(currentCardIndex)}
            />
            {/* Removed non-functional rocket chip/label */}
          </div>

          {/* Navigation Controls */}
          <div className="flex gap-4">
            <div className="relative group">
              <Button
                startIcon={<ArrowLeftIcon />}
                onClick={handlePreviousCard}
                disabled={isPreviousDisabled}
                variant={"filled"}
                size="medium"
                className={`!gap-0 ${
                  isPreviousDisabled ? "text-gray-500" : "text-blue-700"
                }`}
              />
              {isPreviousDisabled && (
                <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 px-3 py-2 bg-gray-800 text-white text-sm rounded-lg opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none whitespace-nowrap z-10">
                  No previous cards available.
                  <div className="absolute top-full left-1/2 transform -translate-x-1/2 w-0 h-0 border-l-4 border-r-4 border-t-4 border-transparent border-t-gray-800"></div>
                </div>
              )}
            </div>
            <div className="relative group">
              <Button
                startIcon={<ArrowRightIcon />}
                variant="filled"
                onClick={handleNextCard}
                disabled={isNextDisabled}
                size="medium"
                className={`!gap-0 ${
                  isNextDisabled ? "text-gray-500" : "text-blue-700"
                }`}
              />
              {isNextDisabled && (
                <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 px-3 py-2 bg-gray-800 text-white text-sm rounded-lg opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none whitespace-nowrap z-10">
                  {currentCardIndex === content?.items?.length - 1
                    ? "No more cards ahead."
                    : "Flip the current card to proceed to next card"}
                  <div className="absolute top-full left-1/2 transform -translate-x-1/2 w-0 h-0 border-l-4 border-r-4 border-t-4 border-transparent border-t-gray-800"></div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default FlashcardPreview;
